export const classNames = (...classes: string[]): string => classes.filter(Boolean).join(" ");

export const capitalize = (str: string) => str?.charAt(0).toUpperCase() + str?.slice(1);

export const toNormalCase = (str: string): string => {
  if (!str) {
    return "";
  }

  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const truncate = (str: string, size: number): string =>
  str?.length <= size ? str : `${str?.slice(0, size)}...`;

const BASE64_IMAGE_PLACEHOLDER = "[Base64 image omitted]";
const BASE64_AUDIO_PLACEHOLDER = "[Base64 audio omitted]";
const BASE64_VIDEO_PLACEHOLDER = "[Base64 video omitted]";
const THOUGHT_SIGNATURE_PLACEHOLDER = "[Thought signature omitted]";
const BASE64_DATA_PLACEHOLDER = "[Base64 data omitted]";

const MIN_BASE64_LENGTH = 500;
const BASE64_REGEX = /^[A-Za-z0-9+/\-_]+=*$/;
const DATA_URL_BASE64_PREFIX = /^data:([^;]+);base64,/i;

const IMAGE_KEYS = new Set(["data", "result", "b64_json", "image_url"]);
const AUDIO_KEYS = new Set(["audio", "audio_data", "audio_url"]);
const THOUGHT_SIGNATURE_KEYS = new Set(["thought_signature", "thoughtSignature"]);

const AUDIO_OUTPUT_FORMATS = new Set(["mp3", "pcm", "wav", "ogg", "flac", "aac", "opus", "m4a"]);
const IMAGE_OUTPUT_FORMATS = new Set(["png", "jpeg", "jpg", "webp", "gif", "bmp", "svg"]);

type MediaKind = "image" | "audio" | "video" | "thought_signature" | "unknown";

interface TruncateContext {
  mediaType?: string;
  mimeType?: string;
  outputFormat?: string;
  type?: string;
}

function looksLikeBase64(s: string): boolean {
  if (typeof s !== "string" || s.length < MIN_BASE64_LENGTH) {
    return false;
  }
  if (DATA_URL_BASE64_PREFIX.test(s)) {
    return true;
  }
  if (!BASE64_REGEX.test(s.trim())) {
    return false;
  }
  return true;
}

function kindFromMime(mime: string | undefined): MediaKind | null {
  if (!mime) {
    return null;
  }
  const normalized = mime.toLowerCase();
  if (normalized.startsWith("image/")) {
    return "image";
  }
  if (normalized.startsWith("audio/")) {
    return "audio";
  }
  if (normalized.startsWith("video/")) {
    return "video";
  }
  return null;
}

function kindFromMediaType(mediaType: string | undefined): MediaKind | null {
  if (!mediaType) {
    return null;
  }
  const normalized = mediaType.toLowerCase();
  if (normalized === "image" || normalized.startsWith("image/")) {
    return "image";
  }
  if (normalized === "audio" || normalized.startsWith("audio/") || normalized === "speech") {
    return "audio";
  }
  if (normalized === "video" || normalized.startsWith("video/")) {
    return "video";
  }
  return null;
}

function kindFromOutputFormat(outputFormat: string | undefined): MediaKind | null {
  if (!outputFormat) {
    return null;
  }
  const normalized = outputFormat.toLowerCase();
  if (AUDIO_OUTPUT_FORMATS.has(normalized)) {
    return "audio";
  }
  if (IMAGE_OUTPUT_FORMATS.has(normalized)) {
    return "image";
  }
  return null;
}

function extendTruncateContext(
  parent: TruncateContext,
  obj: Record<string, unknown>,
): TruncateContext {
  const next = { ...parent };
  if (typeof obj.media_type === "string") {
    next.mediaType = obj.media_type;
  }
  if (typeof obj.mime_type === "string") {
    next.mimeType = obj.mime_type;
  }
  if (typeof obj.output_format === "string") {
    next.outputFormat = obj.output_format;
  }
  if (typeof obj.type === "string") {
    next.type = obj.type;
  }
  return next;
}

function resolveMediaKind(key: string | null, value: string, context: TruncateContext): MediaKind {
  if (key && THOUGHT_SIGNATURE_KEYS.has(key)) {
    return "thought_signature";
  }

  const dataUrlMatch = value.match(DATA_URL_BASE64_PREFIX);
  if (dataUrlMatch?.[1]) {
    const fromDataUrl = kindFromMime(dataUrlMatch[1]);
    if (fromDataUrl) {
      return fromDataUrl;
    }
  }

  const fromMime = kindFromMime(context.mimeType);
  if (fromMime) {
    return fromMime;
  }

  const fromMediaType = kindFromMediaType(context.mediaType);
  if (fromMediaType) {
    return fromMediaType;
  }

  const fromOutputFormat = kindFromOutputFormat(context.outputFormat);
  if (fromOutputFormat) {
    return fromOutputFormat;
  }

  if (key && AUDIO_KEYS.has(key)) {
    return "audio";
  }

  if (key && IMAGE_KEYS.has(key)) {
    // Speech responses reuse images-like `{ data: [{ b64_json }] }` with output_format.
    // Without format/media context, keep the historical image default for these keys.
    return "image";
  }

  if (key === "url" && context.type === "output_media") {
    return "unknown";
  }

  // Long opaque base64 blobs on non-media keys are usually Gemini thought signatures.
  return "thought_signature";
}

function placeholderForMediaKind(kind: MediaKind): string {
  switch (kind) {
    case "image":
      return BASE64_IMAGE_PLACEHOLDER;
    case "audio":
      return BASE64_AUDIO_PLACEHOLDER;
    case "video":
      return BASE64_VIDEO_PLACEHOLDER;
    case "thought_signature":
      return THOUGHT_SIGNATURE_PLACEHOLDER;
    default:
      return BASE64_DATA_PLACEHOLDER;
  }
}

function formatOmittedValue(kind: MediaKind, length: number): string {
  return `${placeholderForMediaKind(kind)} (${length} characters)`;
}

/**
 * Returns a deep copy of obj with long base64 strings replaced by a short placeholder.
 * Use when displaying raw request/response JSON so the UI doesn't render huge base64 blobs.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function truncateRecursive(obj: any, key: string | null, context: TruncateContext): any {
  if (obj === null || typeof obj !== "object") {
    if (typeof obj === "string" && looksLikeBase64(obj)) {
      return formatOmittedValue(resolveMediaKind(key, obj, context), obj.length);
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(v => truncateRecursive(v, key, context));
  }

  const objectContext = extendTruncateContext(context, obj as Record<string, unknown>);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === "string" && looksLikeBase64(v)) {
      out[k] = formatOmittedValue(resolveMediaKind(k, v, objectContext), v.length);
    } else {
      out[k] = truncateRecursive(v, k, objectContext);
    }
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function truncateBase64InObject(obj: any): any {
  return truncateRecursive(obj, null, {});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function forceJSONString(input: any, spaces = 2): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsed: any;

    // If input is a string, try to parse it as JSON
    if (typeof input === "string") {
      try {
        parsed = JSON.parse(input);
      } catch {
        // If parsing fails, return the original string
        return input;
      }
    } else {
      parsed = input;
    }

    // Custom replacer function to handle circular references
    const seen = new WeakSet();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const replacer = (key: string, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return "[Circular]";
        }
        seen.add(value);
      }
      return value;
    };

    // Stringify the input with indentation
    let jsonString = JSON.stringify(parsed, replacer, spaces);

    // Unescape special characters
    jsonString = jsonString
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\");

    // Remove quotes from keys
    jsonString = jsonString.replace(/"([^"]+)":/g, "$1:");

    return jsonString;
  } catch {
    // Handle any unexpected errors

    return String(input);
  }
}

export const snakeToTitleCase = (str: string) =>
  str
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
